import { ArrowLeft, Moon, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const weeklyData = [
  { day: "Mon", hours: 7.5 },
  { day: "Tue", hours: 6.8 },
  { day: "Wed", hours: 8.1 },
  { day: "Thu", hours: 5.9 },
  { day: "Fri", hours: 6.2 },
  { day: "Sat", hours: 7.0 },
  { day: "Sun", hours: 6.2 },
];

const SleepRecovery = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2/inputs")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Sleep Recovery</h1>
        </div>

        {/* Current Value */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-chart-4/10 flex h-14 w-14 items-center justify-center rounded-xl">
              <Moon className="text-chart-4 h-7 w-7" />
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm text-muted-foreground">Last Night</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-foreground">6.2</span>
                <span className="text-lg text-muted-foreground">hrs</span>
              </div>
            </div>
            <Badge className="bg-warning/15 text-warning rounded-full border-0 text-xs">
              Suboptimal
            </Badge>
          </div>
        </Card>

        {/* Sleep Debt */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-2 text-sm font-semibold text-foreground">
            Sleep Debt
          </h3>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">−1.8 hrs</span>
            <span className="text-sm text-muted-foreground">
              below 8hr target
            </span>
          </div>
          <Progress value={77} className="mb-1 h-3" />
          <p className="text-xs text-muted-foreground">
            77% of nightly goal achieved
          </p>
        </Card>

        {/* Weekly Trend */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Weekly Sleep Duration
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  domain={[4, 10]}
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value: number) => [`${value} hrs`, "Sleep"]}
                />
                <ReferenceLine
                  y={8}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="4 4"
                  label={{
                    value: "Goal",
                    fontSize: 10,
                    fill: "hsl(var(--muted-foreground))",
                  }}
                />
                <Bar
                  dataKey="hours"
                  fill="hsl(var(--chart-4))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* What This Means */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What This Means</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Sleep recovery</strong>{" "}
              measures total sleep duration and its adequacy for physiological
              restoration. Insufficient sleep directly increases stress load.
            </p>
            <p>
              <strong className="text-foreground">At 6.2 hours</strong>, you're
              accumulating sleep debt. This reduces your body's ability to
              regulate cortisol, impairs glucose metabolism, and slows muscle
              recovery.
            </p>
            <p>
              <strong className="text-foreground">Cumulative sleep debt</strong>{" "}
              compounds over days. Even one night of poor sleep can elevate your
              stress index for up to 48 hours.
            </p>
          </div>
        </Card>

        {/* Tips */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">How to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-4">•</span>
              <span>Set a consistent bedtime and wake-up alarm</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-4">•</span>
              <span>Avoid screens 1 hour before bed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-4">•</span>
              <span>Keep your bedroom cool (65-68°F / 18-20°C)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-4">•</span>
              <span>Limit caffeine after 2 PM</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SleepRecovery;
